import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { cn } from '../lib/utils'
import { PendingSpinner } from './pending-spinner'

// The app's compact pill action: 36px, fully rounded, 13px/600 label, nudges down on
// press. A distinct "kind" from Button (which is the 36px page/form action), so the two
// stay separate rather than overloading Button's variant list.
const pillButtonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full font-semibold transition-colors active:translate-y-px motion-reduce:active:translate-y-0 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-pelorous-500 dark:focus-visible:ring-pelorous-300 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-3.5 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        // Pinned to fixed ramp steps, NOT the `--brand-green` alias. That alias remaps to
        // brand-green-400 in dark so `text-brand-green` clears AA on dark cards — tuned for
        // brand-green as a FOREGROUND. Used as a FILL it inverts the problem: it left white
        // 14px/600 labels at 3.03:1, under AA. 500 is the only dark step that clears both the
        // label (5.00:1) and the pill's own edge against --card (3.22:1, SC 1.4.11) — 600 and
        // 700 fix the text but sink the edge to 2.32:1 / 1.75:1. Hover darkens in both modes,
        // matching `secondary`.
        primary: 'bg-brand-green-600 text-white hover:bg-brand-green-700 dark:bg-brand-green-600 dark:hover:bg-brand-green-700',
        secondary:
          'border-[1.5px] border-border bg-card text-foreground hover:border-brand-green-border hover:bg-brand-green-50 dark:hover:bg-brand-green-900',
        // Solid destructive action (End plan, delete). Distinct from `danger`, the soft
        // rose status pill below.
        destructive: 'bg-red-500 text-white hover:bg-red-600',
        danger: 'border-[1.5px] border-[color-mix(in_srgb,oklch(var(--red))_45%,transparent)] bg-red-wash text-red hover:border-red',
        // Quiet pill: transparent at rest, a white (card) hover. Reads as a subtle fill
        // on colored page backgrounds, and disappears (white-on-white) on white surfaces
        // like modals — so the same variant suits both.
        ghost: 'bg-transparent text-muted-foreground hover:bg-card hover:text-foreground',
        // "Add another" affordance: ghost shape, green accent. Transparent at rest, so the FILL is
        // the whole affordance — which is why it must read on every surface this lands on. The old
        // `hover:bg-surface` was near-white (#fefcf6): fine on the tan page background, invisible
        // on a card (#1202). Measured against --card as CIE dE76: 3.29 light / 3.76 dark, i.e. below
        // the ~2-10 "noticeable" band at the low end. The tint role is 10.05 / 14.86 — obvious in
        // both — and being a role it remaps per mode, so no `dark:` fill override is needed.
        //
        // `ink` and `on-tint` resolve to the SAME step in both modes (700 light, 300 dark), so the
        // label is already the correct ink for this fill; naming on-tint just says so explicitly and
        // keeps the pair honest if the roles ever diverge. That retires the old hand-tuned
        // `dark:hover:text-brand-green-300`, whose 8.14:1 was measured against the es-surface fill
        // this no longer uses. On the tint the pair is 7.57:1 light / 6.52:1 dark (tokens.css).
        //
        // `active:` matters more than it looks: touch has no hover, so without a press fill the only
        // feedback on mobile was the base `active:translate-y-px` nudge — a 1px shift on a pill with
        // no resting fill. Same tint, so press reads on both input models.
        add: 'bg-transparent text-brand-green-ink hover:bg-brand-green-tint hover:text-brand-green-on-tint active:bg-brand-green-tint active:text-brand-green-on-tint',
        // Quiet destructive affordance (Discard, Delete): ghost shape, rose accent,
        // soft-rose hover. The low-emphasis sibling of the solid `destructive`.
        // Numbered steps with an explicit `dark:`, not the `red` role, because the role is one
        // step too light for text in LIGHT mode: it resolves to red-500, which is 4.56:1 on a
        // card but only 3.62:1 on the cream page — and this variant is transparent, so the page
        // is a surface it lands on. 600 clears both (5.00 / 6.30). Dark needs the opposite: 600
        // is 2.54:1 on a dark card, so it takes 400 (4.88:1). That split is exactly the ramp
        // rule — light-mode text >= 600, dark-mode text <= 400 — which no single step satisfies.
        'ghost-destructive':
          'border border-transparent bg-transparent text-red-600 hover:border-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:border-red-400 dark:hover:bg-red-900',
        // The hover tint is a FIXED palette step, deliberately not a theme-aware token. This pill
        // is `bg-white` in both modes, so anything painted on it must also be mode-independent —
        // `hover:bg-es-green-1` flipped to a dark green under `.dark` while the label stayed
        // near-black, taking the hover state to 1.23:1 and making the label vanish on hover (#1112).
        // `brand-green-100` is the closest fixed step to the old light-mode value, so light is
        // unchanged and dark is 13.19:1.
        //
        // Both on-green variants sit on a green surface, so both override the base ring colour:
        // pelorous is only 1.6-2.9:1 against the brand greens, under the 3:1 SC 1.4.11 wants of a
        // focus indicator. White is 7-21:1 on every green this pair actually lands on (the
        // eating-style hero gradient, es-green-7, the brand-green-900 footer). They differ on the
        // OFFSET, because the ring's inner neighbour differs:
        //
        // `primary` is a WHITE pill, so a flush white ring is 1:1 against its own fill — literally
        // invisible. It keeps a 2px offset, recoloured to brand-green-900: a dark band the white
        // pill reads against (13.4:1) and the white ring reads against (13.4:1). Deliberately NOT
        // matched to the surface — there is no single green to match, the four call sites range
        // from #3d5a38 to black — so it contrasts with both neighbours instead.
        'on-green-primary':
          'bg-white text-brand-green-900 hover:bg-brand-green-100 focus-visible:ring-white dark:focus-visible:ring-white focus-visible:ring-offset-brand-green-900',
        // `secondary` is a translucent fill over the green, so a flush white ring already reads
        // against it (5.2-16.6:1 across the same surfaces). It drops the offset instead: the base
        // `ring-offset-background` would paint a cream band — a stripe of page background cut out
        // of the green — and a dark band would blend into the fill (1.2-2.6:1). Flush is the same
        // call MealCard makes for its over-photo controls.
        'on-green-secondary':
          'border-[1.5px] border-white/35 bg-white/[0.12] text-white hover:bg-white/20 focus-visible:ring-offset-0 focus-visible:ring-white dark:focus-visible:ring-white'
      },
      size: {
        // Dense row actions sitting in a strip beside other controls — the draft card's Swap /
        // Replace / Undo. 28px clears WCAG 2.5.8's 24px minimum but NOT the 44px Apple HIG target,
        // so reach for it only where the action is one of several in a toolbar, never as the
        // primary tap target on a screen. Tightens the base gap too; 8px reads loose at this size.
        sm: 'h-7 gap-1.5 px-2.5 text-xs',
        // Compact (status pills, list-row actions).
        default: 'h-9 px-3.5 text-sm',
        // Larger CTA — 44px touch target.
        lg: 'h-11 px-5 text-sm',
        // Modal footer action (danger tier): a tall 48px tap target on the mobile sheet
        // that drops to the compact 36px default on desktop — mirrors Button size="cta".
        cta: 'h-12 px-5 text-sm md:h-9'
      }
    },
    defaultVariants: { variant: 'primary', size: 'default' }
  }
)

type PillVariant = NonNullable<VariantProps<typeof pillButtonVariants>['variant']>

// Counter badge colors per variant, so a counter reads on any pill without the caller
// having to restyle it. Keyed by the same variant the pill uses.
const COUNTER_TONE: Record<PillVariant, string> = {
  primary: 'bg-white/20 text-white',
  secondary: 'bg-brand-green-tint text-brand-green-on-tint',
  destructive: 'bg-white/20 text-white',
  danger: 'bg-[color-mix(in_srgb,oklch(var(--red))_15%,transparent)] text-red',
  ghost: 'bg-brand-green-tint text-brand-green-on-tint',
  add: 'bg-brand-green-tint text-brand-green-on-tint',
  // Tint background + its ink on top, like every other tone here. Fixed steps rather than the
  // role so the badge carries its own contrast (5.84:1) regardless of the surface behind it.
  'ghost-destructive': 'bg-red-50 text-red-600',
  'on-green-primary': 'bg-brand-green-tint text-brand-green-on-tint',
  'on-green-secondary': 'bg-white/20 text-white'
}

export interface PillButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof pillButtonVariants> {
  /** Leading icon (~14px). */
  icon?: React.ReactNode
  /** Trailing element — e.g. an arrow, a timer, a count. */
  trailing?: React.ReactNode
  /** A count badge after the label, tone-matched to the variant. Hidden when 0 / undefined. */
  counter?: number
  /** Render as the passed child (e.g. a Link) instead of a <button>. */
  asChild?: boolean
  /**
   * An action is in flight: swaps the leading icon for a spinner, disables the button, and marks
   * it `aria-busy`. A STATE, not a variant — it composes with every variant and size.
   *
   * Disabling is part of it, not a separate call: the reason to show a spinner is that something
   * is running, and a second click while it is would fire the same callable twice.
   */
  pending?: boolean
}

const PillButton = React.forwardRef<HTMLButtonElement, PillButtonProps>(
  ({ className, variant = 'primary', size, icon, trailing, counter, asChild = false, pending = false, children, ...props }, ref) => {
    const tone = variant ?? 'primary'
    // Replaces the icon rather than sitting beside it: two glyphs would shift the label mid-action.
    const lead = pending ? <PendingSpinner /> : icon
    const counterBadge =
      counter != null && counter > 0 ? (
        <span
          className={cn(
            'inline-flex items-center justify-center rounded-full px-2 py-0.5 text-xs font-semibold leading-none',
            COUNTER_TONE[tone]
          )}>
          {counter}
        </span>
      ) : null
    const classes = cn(pillButtonVariants({ variant, size, className }))

    // asChild: clone the single child (e.g. a Link) and inject the composed content as
    // ITS children, so Slot only ever receives one element. (Composing icon/counter as
    // Slot siblings via Slottable proved brittle across @radix-ui/react-slot versions.)
    if (asChild && React.isValidElement(children)) {
      return (
        <Slot className={classes} ref={ref} {...props}>
          {React.cloneElement(
            children,
            undefined,
            <>
              {lead}
              {children.props.children}
              {counterBadge}
              {trailing}
            </>
          )}
        </Slot>
      )
    }

    // `disabled` after the spread, deliberately: a caller passing `disabled: undefined` would
    // otherwise clobber it and leave a spinning button still clickable.
    return (
      <button className={classes} ref={ref} {...props} aria-busy={pending || undefined} disabled={pending || props.disabled}>
        {lead}
        {children}
        {counterBadge}
        {trailing}
      </button>
    )
  }
)
PillButton.displayName = 'PillButton'

export { PillButton, pillButtonVariants }
